import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Users } from 'lucide-react';
import { usePersistedBoolean } from '../../hooks/usePersistedBoolean';
import {
  rollupPeople,
  filterRolledUpPeople,
  groupAndSortRolledUpPeople,
  type RolledUpPerson,
} from '../../lib/peopleRollup';
import { PersonContactRow } from './PersonContactRow';
import type { OrganizationWithPeople } from '../../types';
import { formatSportLabel } from '../../lib/sportLabel';

const SECTION_KEY = 'crm:orgPage:contactsSection:expanded';

interface CoachesContactsSectionProps {
  organization: OrganizationWithPeople;
  onAddPerson?: (sportId: string | null, teamId: string | null) => void;
  onScheduleActivity?: (personId: string, personName: string) => void;
  onConvert?: (personId: string, teamId: string, sportId: string) => void;
  canEdit?: boolean;
}

export const CoachesContactsSection = ({
  organization,
  onAddPerson,
  onScheduleActivity,
  onConvert,
  canEdit = false,
}: CoachesContactsSectionProps) => {
  const [isExpanded, setIsExpanded] = usePersistedBoolean(SECTION_KEY, true);
  const [query, setQuery] = useState('');
  const [sportId, setSportId] = useState<string | null>(null);

  const rolled = useMemo(() => rollupPeople(organization), [organization]);

  const sportOptions = useMemo(
    () =>
      organization.sports
        .filter((s) => s.isActive)
        .map((s) => ({ id: s.id, label: formatSportLabel(s.gender, s.name) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [organization.sports],
  );

  const distinctSportCount = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rolled) {
      if (r.isFormer) continue;
      for (const link of r.displayLinks) {
        if (link.scope === 'SPORT' && link.sportId) ids.add(link.sportId);
      }
    }
    return ids.size;
  }, [rolled]);

  const filtered = useMemo(
    () => filterRolledUpPeople(rolled, { query, sportId }),
    [rolled, query, sportId],
  );

  // CRM-33: the org payload includes former (end-dated) links; this section
  // shows CURRENT people only. Former coaches are revealed per-team in the
  // Sports & Teams section.
  const currentRolled = useMemo(() => filtered.filter((r) => !r.isFormer), [filtered]);

  const grouped = useMemo(() => groupAndSortRolledUpPeople(currentRolled), [currentRolled]);

  const totalFiltered = grouped.coaches.length + grouped.sportStaff.length + grouped.orgStaff.length;
  const hasActiveFilter = query.trim() !== '' || sportId !== null;

  const renderGroup = (label: string, rows: RolledUpPerson[]) => {
    if (rows.length === 0) return null;
    return (
      <div>
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/40 border-t border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {label} ({rows.length})
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {rows.map((r) => (
            <PersonContactRow
              key={r.person.id}
              rolled={r}
              onScheduleActivity={onScheduleActivity}
              onConvert={onConvert}
              canEdit={canEdit}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => {
          setIsExpanded(!isExpanded);
          if (isExpanded) {
            setQuery('');
            setSportId(null);
          }
        }}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <Users className="h-5 w-5 text-blast-600 dark:text-blast-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            Coaches &amp; Contacts
          </h2>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
          {currentRolled.length} {currentRolled.length === 1 ? 'person' : 'people'}
          {' · '}
          {distinctSportCount} {distinctSportCount === 1 ? 'sport' : 'sports'}
        </span>
      </button>

      {isExpanded && (
        <>
          {/* Filter row */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 border-t border-gray-200 dark:border-gray-700 px-5 py-3 bg-gray-50 dark:bg-gray-800/30">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, role, email, phone…"
              className="input flex-1 sm:min-w-0"
              aria-label="Filter contacts by text"
            />
            <div className="flex items-center gap-2 sm:flex-shrink-0">
              <select
                value={sportId ?? ''}
                onChange={(e) => setSportId(e.target.value || null)}
                className="input"
                aria-label="Filter by sport"
              >
                <option value="">All sports</option>
                {sportOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              {canEdit && onAddPerson && (
                <button
                  type="button"
                  onClick={() => onAddPerson(null, null)}
                  className="btn-primary inline-flex items-center gap-1 flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                  Add Person
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          {totalFiltered === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {hasActiveFilter ? (
                <>
                  No contacts match these filters.{' '}
                  <button
                    type="button"
                    onClick={() => { setQuery(''); setSportId(null); }}
                    className="text-blast-600 hover:underline"
                  >
                    Clear filters
                  </button>
                </>
              ) : (
                'No contacts yet.'
              )}
            </div>
          ) : (
            <>
              {renderGroup('Coaches', grouped.coaches)}
              {renderGroup('Sport Staff', grouped.sportStaff)}
              {renderGroup('Org Staff', grouped.orgStaff)}
            </>
          )}
        </>
      )}
    </div>
  );
};
